import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import { getStatusCardStyles } from "./SlotCard";
import apiServices from "../../../../../services/apiService";

const ParkingDetailsPanel = ({ slots, onClose, onAllocate, onRelease, roleId }) => {
    const [assignmentData, setAssignmentData] = useState(null);
    const [loadingAssignment, setLoadingAssignment] = useState(false);

    const isMulti = slots?.length > 1;
    const primarySlot = slots?.[0];
    const cardStyles = getStatusCardStyles(primarySlot?.status || 'Available');

    useEffect(() => {
        const fetchAssignment = async () => {
            if (!isMulti && primarySlot && primarySlot.status?.toLowerCase() === "occupied") {
                setLoadingAssignment(true);
                try {
                    const res = await apiServices({
                        method: "GET",
                        url: `/ParkingAssignment?slotId=${primarySlot.id}`
                    });
                    if (res.success && res.data?.Data && res.data.Data.length > 0) {
                        setAssignmentData(res.data.Data[0]);
                    } else {
                        setAssignmentData(null);
                    }
                } catch (err) {
                    console.error("Failed to fetch assignment details:", err);
                    setAssignmentData(null);
                } finally {
                    setLoadingAssignment(false);
                }
            } else {
                setAssignmentData(null);
            }
        };

        fetchAssignment();
    }, [isMulti, primarySlot]);

    if (!slots || slots.length === 0) return null;

    return (
        <div className="w-[280px] bg-white dark:bg-dashboardbg border-l border-gray-200 dark:border-bordercolor shadow-lg lg:shadow-none flex flex-col absolute lg:static right-0 top-0 z-10 lg:z-auto h-full lg:h-auto transition-transform duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-bordercolor">
                <h2 className="text-sm font-bold text-gray-800 dark:text-textprimary">Parking Details</h2>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-textprimary transition-colors"
                >
                    <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto lg:overflow-y-visible p-3">
                <div className="flex items-center justify-between mb-3 gap-2">
                    <h3 className="text-base font-bold text-blue-600 dark:text-blue-400 truncate">
                        {isMulti ? `${slots.length} Slots Selected` : (primarySlot.slotNumber || primarySlot.groupName)}
                    </h3>
                    {!isMulti && (
                        <div className={`px-2 py-0.5 rounded text-xs font-bold capitalize border ${cardStyles}`}>
                            {primarySlot.status}
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    {/* Location */}
                    <div>
                        <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Location</h4>
                        {isMulti ? (
                            <div className="flex flex-col gap-1 text-[10px]">
                                <span className="text-gray-500 dark:text-textsecondary">Selected IDs:</span>
                                <span className="font-semibold text-gray-800 dark:text-textprimary break-words">
                                    {slots.map(s => s.slotNumber).join(", ")}
                                </span>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Floor</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.floorName || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-gray-500 dark:text-textsecondary">Allocation Type</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.allocationType || "General"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-gray-500 dark:text-textsecondary">Allocated Unit Name</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.allocatedUnitName || "—"}</span>
                                </div>
                            </>
                        )}
                    </div>

                    <hr className="border-gray-100 dark:border-bordercolor" />

                    {/* Rental & Attributes */}
                    {!isMulti && (
                        <>
                            <div>
                                <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Parking Attributes</h4>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Type</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.parkingTypeName || primarySlot.type || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">EV Charging</span>
                                    <span className={`font-semibold ${primarySlot.hasChargingPoint ? 'text-green-600' : 'text-gray-800 dark:text-textprimary'}`}>
                                        {primarySlot.hasChargingPoint ? 'Available' : 'None'}
                                    </span>
                                </div>
                                {(primarySlot.rentalStatus || primarySlot.originalOwnerUnitName) && (
                                    <>
                                        <div className="flex flex-col gap-1.5 mt-2 border-t pt-2 border-dashed border-gray-100 dark:border-bordercolor">
                                            <div className="flex items-center justify-between text-[10px]">
                                                <span className="text-gray-500 dark:text-textsecondary">Rental Status</span>
                                                <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.rentalStatus || "—"}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-[10px]">
                                                <span className="text-gray-500 dark:text-textsecondary">Original Owner</span>
                                                <span className="font-semibold text-gray-800 dark:text-textprimary text-right break-words max-w-[120px]">{primarySlot.originalOwnerUnitName || "—"}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-[10px]">
                                                <span className="text-gray-500 dark:text-textsecondary">Current Renter</span>
                                                <span className="font-semibold text-gray-800 dark:text-textprimary text-right break-words max-w-[120px]">{primarySlot.currentRenterUnitName || "—"}</span>
                                            </div>
                                            {primarySlot.pricingPerHour && (
                                                <div className="flex items-center justify-between text-[10px]">
                                                    <span className="text-gray-500 dark:text-textsecondary">Price / Hour</span>
                                                    <span className="font-semibold text-green-600">₹{primarySlot.pricingPerHour}</span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            <hr className="border-gray-100 dark:border-bordercolor" />
                        </>
                    )}

                    {/* Assignment */}
                    {!isMulti && primarySlot?.status?.toLowerCase() === "occupied" && (
                        <>
                            <div>
                                <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Assignment</h4>
                                {loadingAssignment ? (
                                    <div className="text-[10px] text-gray-400 animate-pulse py-2">Loading details...</div>
                                ) : assignmentData ? (
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">Assigned Unit</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary text-right break-words max-w-[140px]">{assignmentData.UnitName || "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">Assigned User</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{assignmentData.AssignedUserName || "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">Vehicle</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{assignmentData.VehicleNumber || "—"} {assignmentData.VehicleType ? `(${assignmentData.VehicleType})` : ""}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">Mobile Number</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{assignmentData.MobileNumber || "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">Start Date</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{assignmentData.StartDate ? new Date(assignmentData.StartDate).toLocaleDateString() : "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">End Date</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{assignmentData.EndDate ? new Date(assignmentData.EndDate).toLocaleDateString() : "—"}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between text-[10px] mb-1.5">
                                            <span className="text-gray-500 dark:text-textsecondary">Assigned Unit</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.assignedUnit || "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px] mb-1.5">
                                            <span className="text-gray-500 dark:text-textsecondary">Current Vehicle</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.currentVehicle || "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px] mb-1.5">
                                            <span className="text-gray-500 dark:text-textsecondary">Current User</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.currentUser || "—"}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px] mb-1.5">
                                            <span className="text-gray-500 dark:text-textsecondary">Current Status</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary capitalize">{primarySlot.status}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px]">
                                            <span className="text-gray-500 dark:text-textsecondary">Last Updated</span>
                                            <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.lastUpdated || "—"}</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            <hr className="border-gray-100 dark:border-bordercolor" />
                        </>
                    )}

                    {/* Quick Actions */}
                    <div>
                        <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Quick Actions</h4>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={onAllocate}
                                disabled={slots.some(s => s.status?.toLowerCase() === "occupied" || (roleId !== 4 && s.status?.toLowerCase() === "allocated") || s.rentalStatus === 'Rented')}
                                className={`flex flex-col items-center justify-center p-1.5 rounded-lg border transition-all duration-200 
                                    ${slots.some(s => s.status?.toLowerCase() === "occupied" || (roleId !== 4 && s.status?.toLowerCase() === "allocated") || s.rentalStatus === 'Rented')
                                        ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-bordercolor bg-gray-50 dark:bg-actionitembg"
                                        : "border-gray-200 dark:border-bordercolor bg-white dark:bg-actionitembg hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
                                    }`}
                            >
                                <FontAwesomeIcon icon={faCheck} className="mb-0.5 text-xs" />
                                <span className="text-[9px] font-semibold">Allocate</span>
                            </button>

                            <button
                                onClick={onRelease}
                                disabled={slots.every(s => s.status?.toLowerCase() === "available" && s.rentalStatus !== "Rented")}
                                className={`flex flex-col items-center justify-center p-1.5 rounded-lg border transition-all duration-200 
                                    ${slots.every(s => s.status?.toLowerCase() === "available" && s.rentalStatus !== "Rented")
                                        ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-bordercolor bg-gray-50 dark:bg-actionitembg"
                                        : "border-gray-200 dark:border-bordercolor bg-white dark:bg-actionitembg hover:border-red-500 hover:text-red-600 dark:hover:text-red-400"
                                    }`}
                            >
                                <FontAwesomeIcon icon={faTimes} className="mb-0.5 text-xs" />
                                <span className="text-[9px] font-semibold">Release</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParkingDetailsPanel;
